
import { Component, Vue } from "vue-property-decorator";
import VueFilters from "@/vue-filters";
import { Action, Getter } from "vuex-class";
import IconEdit from "@/components/common/icon/IconEdit.vue";
import {
  EDirection,
  EUserType,
  IInformationOptions,
  IPageResponse,
  IUserRequest,
  IUsersReportRequest,
} from "@/services";
import Pagination from "@/components/common/Pagination.vue";
import { ICountry, IUser } from "@/entities";
import FilterUsers from "@/components/user/FilterUsers.vue";
import { EventBus } from "@/event-bus";
import { Dictionary } from "vue-router/types/router";
import { i18n } from "@/i18n";
import { BvTableFieldArray } from "bootstrap-vue";
import { SweetAlertResult } from "sweetalert2";

@Component({
  name: "users-table",
  mixins: [VueFilters],
  components: {
    IconEdit,
    Pagination,
    FilterUsers,
  },
})
export default class UsersTable extends Vue {
  @Action("fetchUsersPage") fetchUsersPage!: (
    params: IUserRequest
  ) => Promise<IPageResponse<IUser> | undefined>;
  @Action("storeUsersReport") storeUsersReport!: (params: IUsersReportRequest) => Promise<void>;
  @Getter("getLoggedUser") loggedUser: IUser | undefined;
  @Getter("getCountriesPage") countriesPage: IPageResponse<ICountry> | undefined;

  private users: IPageResponse<IUser> | undefined = {
    content: [] as IUser[],
  } as IPageResponse<IUser>;
  public countrySelected: ICountry | null = null;
  private queryParams = this.$route.query;

  public serviceParams: IUserRequest = {
    id: this.queryParams.id ? Number(this.queryParams.id) : undefined,
    admin: this.queryParams.admin ? this.queryParams.admin === "true" : undefined,
    countryId: this.queryParams.countryId ? Number(this.queryParams.countryId) : undefined,
    creationDate: this.queryParams.creationDate?.toString(),
    email: this.queryParams.email?.toString(),
    lastname: this.queryParams.lastname?.toString(),
    modificationDate: this.queryParams.modificationDate?.toString(),
    name: this.queryParams.name?.toString(),
    oid: this.queryParams.oid?.toString(),
    username: this.queryParams.username?.toString(),
    userType: this.getQueryParamsUserType(this.queryParams),
    client: this.queryParams.client?.toString(),
    subClient: this.queryParams.subClient?.toString(),
    servicePoint: this.queryParams.servicePoint?.toString(),
    idUserPrecise: this.queryParams.idUserPrecise?.toString() === "true" || false,
    namePrecise: this.queryParams.namePrecise?.toString() === "true" || false,
    lastnamePrecise: this.queryParams.lastnamePrecise?.toString() === "true" || false,
    emailPrecise: this.queryParams.emailPrecise?.toString() === "true" || false,
    clientPrecise: this.queryParams.clientPrecise?.toString() === "true" || false,
    subclientPrecise: this.queryParams.subclientPrecise?.toString() === "true" || false,
    servicePointPrecise: this.queryParams.servicePointPrecise?.toString() === "true" || false,
  };

  get informationOptions(): IInformationOptions {
    return {
      includeUser: true,
      includeFirstName: true,
      includeLastName: true,
      includeEmail: true,
      includeCountry: this.loggedUser?.admin || false,
      includeSupervisor: true,
      includeAdministrator: this.loggedUser?.admin || false,
      includeUserType: this.loggedUser?.userType === EUserType.PROSEGUR,
      includeClients: true,
      includeSubClients: true,
      includeServicePoints: true,
      includeActiveStatus: true,
      includeSolCreatedStatus: true,
      includeSolSynchronizedStatus: true,
      includeCreationDate: true,
      includeModules: true,
    } as IInformationOptions;
  }

  get serviceUsersReportParams(): IUsersReportRequest {
    return {
      filterOptions: this.serviceParams,
      informationOptions: this.informationOptions,
    } as IUsersReportRequest;
  }

  pageNumber = Number(this.queryParams.page) || 1;
  pageSize = 10;
  totalElements = 0;
  orderBy = this.queryParams.orderBy?.toString() || "id";
  sortDesc = this.queryParams.direction !== "ASC";
  active = this.queryParams.active !== "false";

  constructor() {
    super();
  }

  mounted(): void {
    if (this.loggedUser?.admin) {
      this.countrySelected = this.loggedUser?.country || null;
    }
    this.getUsers();
    EventBus.$on("update-users-table", () => {
      this.onChangeCurrentPage(1);
    });
  }

  getQueryParamsUserType(
    queryParams: Dictionary<string | (string | null)[]>
  ): EUserType | undefined {
    if (queryParams.userType === "CLIENTE") {
      return EUserType.CLIENTE;
    }

    return queryParams.userType === "PROSEGUR" ? EUserType.PROSEGUR : undefined;
  }

  get showInactive(): boolean {
    return !this.active;
  }

  set showInactive(value: boolean) {
    this.active = !value;
  }

  async beforeDestroy(): Promise<void> {
    EventBus.$off("update-users-table");
  }

  async filterUsers(): Promise<void> {
    this.$nextTick(async () => this.getUsers());
  }

  async onChangeCurrentPage(currentPage: number): Promise<void> {
    this.pageNumber = currentPage;
    await this.getUsers();
  }

  async getUsers(): Promise<void> {
    this.serviceParams.active = this.active;
    this.serviceParams.size = this.pageSize;
    this.serviceParams.orderBy = this.orderBy || undefined;
    this.serviceParams.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
    this.serviceParams.page = this.pageNumber;
    await this.$router
      .replace({ path: this.$route.path, query: this.serviceParams as Dictionary<string> })
      .catch(() => {
        // do nothing.
      });
    this.serviceParams.page = this.pageNumber - 1;
    this.users = await this.fetchUsersPage(this.serviceParams);

    this.totalElements = this.users?.totalElements || 0;
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.confirmation.to.export.message").toString(),
      text: this.$t("label.confirmation.to.export.message.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }

  private async generateUsersReport(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      this.serviceUsersReportParams.filterOptions = this.serviceParams;
      await this.storeUsersReport(this.serviceUsersReportParams);
      this.makeToast("success", i18n.t("message.usersreport.ok").toString());
    }
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
      },
      {
        key: "userType",
        label: this.$t("column.type") as string,
        sortable: true,
        thClass: this.$ability.can("read", "UserTypes") ? "table-header" : "table-header d-none",
        tdClass: this.$ability.can("read", "UserTypes")
          ? "table-cell-middle"
          : "table-cell-middle d-none",
        formatter: (value: string | undefined) =>
          value ? this.$t(`option.type.${value?.toLowerCase()}`) : "",
      },
      {
        key: "country.code",
        label: this.$t("column.country") as string,
        sortable: true,
        thClass: this.$ability.can("read", "Countries") ? "table-header" : "table-header d-none",
        tdClass: this.$ability.can("read", "Countries")
          ? "table-cell-middle"
          : "table-cell-middle d-none",
        formatter: (value: string | undefined) => this.$t(`country.${value?.toLowerCase()}`),
      },
      {
        key: "username",
        label: this.$t("column.username") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "name",
        label: this.$t("column.name") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "lastName",
        label: this.$t("column.lastname") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "email",
        label: this.$t("column.email") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "createdAt",
        label: this.$t("column.creation_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "updatedAt",
        label: this.$t("column.update_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }
}
